import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import {
  OnboardingStage,
  OnboardingStageWorkflow
} from "@/pages/settings/components/contractsSettings/types";
import StageWorkflowsEmptyState from "@/components/shared/StageWorkflowsEmptyState";
import OnboardingStagesMenu from "./components/OnboardingStagesMenu";
import OnboardingWorkflowDetailPanel from "./components/OnboardingWorkflowDetailPanel";
import OnboardingStagesPanelFooter from "./components/OnboardingStagesPanelFooter";
import OnboardingCompletedEmptyState from "./components/OnboardingCompletedEmptyState";
import { usePrefetchAllWorkflowResponses } from "@/pages/contracts/hooks";

interface Props {
  contractId: string;
  stages: OnboardingStage[];
  currentStageId: string | null;
  onProceedStage: (stageId: string) => void;
  onComplete: () => void;
  workflowToOpen?: string | null;
  onWorkflowOpened?: () => void;
  isCompleted?: boolean;
}

const OnboardingStagesPanel: React.FC<Props> = ({ contractId, stages, currentStageId, onProceedStage, onComplete, workflowToOpen, onWorkflowOpened, isCompleted = false }) => {
  const { t } = useTranslation();

  const allWorkflows = useMemo<OnboardingStageWorkflow[]>(
    () => stages.flatMap((stage) => stage.workflows ?? []),
    [stages]
  );

  const allWorkflowIds = useMemo(
    () => allWorkflows.map((workflow) => workflow.id),
    [allWorkflows]
  );
  usePrefetchAllWorkflowResponses(contractId, allWorkflowIds);

  const effectiveCurrentStageId = useMemo<string | null>(() => {
    if (stages.some((stage) => stage.id === currentStageId)) {
      return currentStageId;
    }
    return stages[0]?.id ?? null;
  }, [stages, currentStageId]);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<
    string | undefined
  >(undefined);

  const prevStageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isCompleted) return;
    if (effectiveCurrentStageId === prevStageIdRef.current) return;
    prevStageIdRef.current = effectiveCurrentStageId;
    const stage = stages.find((stg) => stg.id === effectiveCurrentStageId);
    setSelectedWorkflowId(stage?.workflows?.[0]?.id);
  }, [effectiveCurrentStageId, stages, isCompleted]);

  useEffect(() => {
    if (!workflowToOpen) return;
    if (allWorkflows.some((workflow) => workflow.id === workflowToOpen)) {
      setSelectedWorkflowId(workflowToOpen);
    }
    onWorkflowOpened?.();
  }, [workflowToOpen, allWorkflows, onWorkflowOpened]);

  const selectedWorkflow = useMemo(
    () => allWorkflows.find((workflow) => workflow.id === selectedWorkflowId),
    [allWorkflows, selectedWorkflowId]
  );

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
  };

  const handleStageSelect = (stageId: string) => {
    const stage = stages.find((stg) => stg.id === stageId);
    setSelectedWorkflowId(stage?.workflows?.[0]?.id);
  };

  return (
    <div
      className="flex gap-4 xl:gap-6 xxl:gap-10 h-[71vh] justify-center"
      data-test="contract-detailed-view-onboarding-stages-panel"
    >
      <div className="flex flex-col min-w-[320px] lg:min-w-[360px] xxl:min-w-[420px] h-full justify-between">
        <OnboardingStagesMenu
          contractId={contractId}
          stages={stages}
          currentStageId={effectiveCurrentStageId}
          selectedWorkflowId={selectedWorkflowId}
          onStageSelect={handleStageSelect}
          onWorkflowSelect={handleWorkflowSelect}
          isCompleted={isCompleted}
        />
        {isCompleted ? null : (
          <OnboardingStagesPanelFooter
            stages={stages}
            currentStageId={effectiveCurrentStageId}
            onProceedStage={onProceedStage}
            onComplete={onComplete}
          />
        )}
      </div>
      <div
        className={cn(
          "w-full h-full bg-surface-widget-card border border-border-border1 rounded-2xl xl:max-w-[1280px]",
          (isCompleted || !selectedWorkflow) && "bg-transparent border-transparent"
        )}
      >
        {isCompleted ? (
          <OnboardingCompletedEmptyState />
        ) : selectedWorkflow ? (
          <OnboardingWorkflowDetailPanel contractId={contractId} workflow={selectedWorkflow} />
        ) : (
          <StageWorkflowsEmptyState
            title={t(
              "contracts.detailedView.onboarding.noWorkflowsForStageTitle"
            )}
            subtitle={t(
              "contracts.detailedView.onboarding.noWorkflowsForStageSubtitle"
            )}
          />
        )}
      </div>
    </div>
  );
};

export default OnboardingStagesPanel;
